import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { SiteBannerBar, isSafeBannerUrl } from '../../components/SiteBanner';
import { clearSiteBannerCache } from '../../hooks/useSiteBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_MESSAGE_LENGTH = 250;

const EMPTY_BANNER = { is_enabled: true, message: '', link_text: '', link_url: '' };

const toFormState = (data) => ({
  is_enabled: !!data.is_enabled,
  message: data.message || '',
  link_text: data.link_text || '',
  link_url: data.link_url || '',
});

const formatUpdated = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const SiteBannerSettings = () => {
  const [form, setForm] = useState(EMPTY_BANNER);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/site-banner`, { withCredentials: true })
      .then((res) => {
        setForm(toFormState(res.data));
        setUpdatedAt(res.data.updated_at);
      })
      .catch(() => toast.error('Failed to load the banner'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const linkUrl = form.link_url.trim();
  const linkText = form.link_text.trim();
  const urlInvalid = !!linkUrl && !isSafeBannerUrl(linkUrl);
  const linkTextMissing = !!linkUrl && !!linkText && !form.message.includes(linkText);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.is_enabled && !form.message.trim()) {
      toast.error('Enter the banner message, or switch the banner off');
      return;
    }
    if (urlInvalid) {
      toast.error('Link URL must start with https://, http:// or /');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/site-banner`,
        { ...form, link_text: linkText, link_url: linkUrl },
        { withCredentials: true }
      );
      setForm(toFormState(data));
      setUpdatedAt(data.updated_at);
      clearSiteBannerCache();
      toast.success('Banner saved. It is live on the website now.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg?.replace(/^Value error, /, '') : detail || 'Failed to save the banner');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Site Banner</h1>
        <p className="text-sm text-gray-500 mt-1">
          The black bar at the top of every page of the website. Changes go live as soon as you save — no deployment needed.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl bg-white rounded-lg border p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Switch checked={form.is_enabled} onCheckedChange={(v) => handleChange('is_enabled', v)} id="banner-enabled" />
          <Label htmlFor="banner-enabled">Show the banner on the website</Label>
        </div>

        <div>
          <Label htmlFor="banner-message">Message *</Label>
          <Textarea
            id="banner-message"
            value={form.message}
            onChange={(e) => handleChange('message', e.target.value)}
            rows={2}
            maxLength={MAX_MESSAGE_LENGTH}
            placeholder="Click here to read our latest newsletter"
          />
          <p className="text-xs text-gray-500 mt-1">
            Keep it short so it fits on a phone. {form.message.length}/{MAX_MESSAGE_LENGTH} characters.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="banner-link-text">Words to hyperlink</Label>
            <Input
              id="banner-link-text"
              value={form.link_text}
              onChange={(e) => handleChange('link_text', e.target.value)}
              placeholder="here"
            />
            <p className="text-xs text-gray-500 mt-1">
              Must match the wording in the message exactly. Leave blank to link the whole message.
            </p>
          </div>
          <div>
            <Label htmlFor="banner-link-url">Link URL</Label>
            <Input
              id="banner-link-url"
              value={form.link_url}
              onChange={(e) => handleChange('link_url', e.target.value)}
              placeholder="https://yournest.in/resources/newsletters/..."
            />
            <p className="text-xs text-gray-500 mt-1">
              A full https:// link, or a page on this site like /investor-alert. Leave blank for no link.
            </p>
          </div>
        </div>

        {urlInvalid && (
          <p className="text-sm text-red-600" role="alert">
            The link URL must start with https://, http:// or /.
          </p>
        )}
        {linkTextMissing && (
          <p className="text-sm text-amber-700" role="alert">
            &quot;{linkText}&quot; isn&apos;t in the message, so the whole message will be linked instead.
          </p>
        )}

        <div>
          <div className="text-xs font-medium text-gray-500 mb-2">Preview</div>
          <div className="rounded-lg overflow-hidden border border-gray-200">
            {form.is_enabled && form.message.trim() ? (
              <SiteBannerBar banner={{ ...form, link_text: linkText, link_url: urlInvalid ? '' : linkUrl }} />
            ) : (
              <div className="bg-gray-50 text-sm text-gray-500 text-center py-3">
                {form.is_enabled ? 'Type a message to see the preview.' : 'The banner is hidden on the website.'}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-gray-500">
            {formatUpdated(updatedAt) ? `Last saved ${formatUpdated(updatedAt)}` : 'Not edited yet — showing the default banner.'}
          </p>
          <Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save banner
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SiteBannerSettings;
